import { User, Lock, Eye, EyeOff } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiCall, encryptPassword } from '../CommonComponents/CustomHooks';
import { SetSession } from '../CommonComponents/criptoUtils';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import BlurLoader from '../CommonComponents/BlurFullPageLoader';

const Login = () => {
    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [loginData, setLoginData] = useState({ userCode: '', password: '' });

    const handleSignIn = async () => {
        setIsLoading(true);
        try {
            const res = await ApiCall(`/api/UserLogin/Login`, {
                ...loginData,
                password: encryptPassword(loginData.password),
                rememberMe
            });
            if (res.status === 'SUCCESS') {
                SetSession('cookies', 'user', res?.loginDetails);
                SetSession('localstorage', 'profile', res?.profilePic);
                setTimeout(() => {
                    setIsLoading(false);
                    navigate('/Dashboard/Dashboard');
                    localStorage.setItem('ID', '1');
                    localStorage.setItem('ACTIVE_SUBMENU', '0');
                }, 700);
            } else {
                setIsLoading(false);
                NotificationSound(res.status, res.message, res.focus, inputRefs);
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Login failed', error);
            NotificationSound('ERROR', 'An unexpected error occurred. Please try again later.');
        }
    };

    useEffect(() => {
        inputRefs.current['txtUserCode']?.focus();
        document.title = 'GRANDSLAM_LEAD : Login';
    }, []);

    return (
        <>
            {/* Full screen background */}
            <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-blue-50 flex items-center justify-center p-6 relative overflow-hidden">

                {/* Background blobs */}
                <div className="absolute top-10 left-10 w-64 h-64 bg-[#00adef]/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-10 right-10 w-72 h-72 bg-[#ec1f24]/10 rounded-full blur-3xl pointer-events-none" />

                {/* Card */}
                <div className="max-w-5xl w-full bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden flex min-h-[520px]">

                    {/* ????? LEFT PANEL ????? */}
                    <div className="w-7/12 relative bg-gradient-to-br from-[#93C5FD] to-[#054a91] flex items-center justify-center p-10 overflow-hidden">

                        {/* Decorative circles */}
                        <div className="absolute top-[-60px] right-[-60px] w-56 h-56 bg-white/10 rounded-full blur-2xl" />
                        <div className="absolute bottom-[-40px] left-[-40px] w-36 h-36 bg-[#0655A3]/20 rounded-full blur-xl" />
                        <div className="absolute top-[38%] left-7 w-20 h-20 bg-white/10 rounded-full blur-lg" />

                        {/* Glass overlay (premium feel) */}
                        <div className="absolute inset-0 backdrop-blur-[2px]" />

                        {/* Content Center */}
                        <div className="relative z-10 flex flex-col items-center text-center">

                            {/* Logo */}
                            <img
                                src="./main-logo.png"
                                alt="logo"
                                className="w-70 h-70 object-contain mb-6 drop-shadow-xl transition hover:scale-105"
                            />

                            {/* Brand Name */}
                            <h2 className="text-white text-lg tracking-widest font-medium mb-2">
                                GRANDSLAMLead
                            </h2>

                            {/* Title */}
                            <h1 className="text-4xl font-semibold text-white leading-tight mb-3">
                                Welcome Back!
                            </h1>

                            {/* Description */}
                            <p className="text-white/80 text-sm leading-relaxed max-w-xs">
                                Lead Management System  track, manage and grow your business pipeline with ease.
                            </p>

                            {/* Tags */}
                            <div className="flex flex-wrap justify-center gap-2 mt-6">
                                {['Lead Management', 'Lead Tracking'].map(tag => (
                                    <span
                                        key={tag}
                                        className="text-[11px] text-white/90 bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1 rounded-full hover:bg-white/20 transition"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>

                        </div>
                    </div>

                    {/* ????? RIGHT PANEL ????? */}
                    <div className="w-5/12 p-10 flex flex-col justify-center bg-white">
                        <div className="flex ">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-1">Sign in</h2>
                        <img
                            src="./main-logo.png"   
                            alt="logo"
                            className="h-8 ml-3 mt-0 mb-2 object-contain"
                        />
                        </div>
                        <p className="text-gray-500 text-xs mb-7">
                            Lead Management System &nbsp;
                            <span className="text-[#0655A3] font-medium">GRANDSLAM LEAD</span>
                        </p>

                        <div className="space-y-4">

                            {/* User Code */}
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Enter User Code"
                                    id="txtUserCode"
                                    ref={el => (inputRefs.current['txtUserCode'] = el)}
                                    value={loginData.userCode}
                                    onChange={e => setLoginData({ ...loginData, userCode: e.target.value })}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            inputRefs.current['txtPassword']?.focus();
                                        }
                                    }}
                                    className="w-full pl-10 pr-4 py-3 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00adef]/40 focus:border-[#00adef] transition-all"
                                />
                            </div>

                            {/* Password */}
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Password"
                                    id="txtPassword"
                                    ref={el => (inputRefs.current['txtPassword'] = el)}
                                    value={loginData.password}
                                    onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') inputRefs.current['btnLogin']?.focus();
                                    }}
                                    className="w-full pl-10 pr-12 py-3 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00adef]/40 focus:border-[#00adef] transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#054a91] hover:text-[#0655A3] transition-colors"
                                >
                                    {showPassword
                                        ? <EyeOff className="w-4 h-4" />
                                        : <Eye className="w-4 h-4" />}
                                </button>
                            </div>

                            {/* Remember me + Forgot */}
                            <div className="flex items-center justify-between text-xs">
                                <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={e => setRememberMe(e.target.checked)}
                                        className="w-3.5 h-3.5 accent-[#0655A3] rounded"
                                    />
                                    Remember me
                                </label>
                                {/*<Link*/}
                                {/*    to="forgotpassword"*/}
                                {/*    className="text-[#0655A3] hover:text-[#054a91] font-medium transition-colors"*/}
                                {/*>*/}
                                {/*    Forgot Password?*/}
                                {/*</Link>*/}
                            </div>

                            {/* Submit */}
                            <button
                                ref={el => (inputRefs.current['btnLogin'] = el)}
                                onClick={handleSignIn}
                                className="w-full bg-gradient-to-r from-[#0655A3] to-[#0ea5e9] hover:from-[#054a91] hover:to-[#0284c7] active:from-[#043b73] active:to-[#0369a1] text-white py-3 text-sm rounded-xl font-medium transition-all shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                Sign in
                            </button>

                            {/* Divider */}
                            <div className="flex items-center gap-3 pt-1">
                                <div className="flex-1 h-px bg-gray-100" />
                                <span className="text-[11px] text-gray-400">secure login</span>
                                <div className="flex-1 h-px bg-gray-100" />
                            </div>

                            <p className="text-center text-[11px] text-gray-400">
                                2026 Grandslam Lead. All rights reserved.
                            </p>
                        </div>
                    </div>

                </div>
            </div>

            {isLoading && <BlurLoader />}
        </>
    );
};

export default Login;